/**
 * UISkinContext — orthogonal visual-language axis.
 *
 * Distinct from NavMode (which controls sidebar layout). UISkin governs the
 * *visual language* applied across opted-in surfaces:
 *   • "classic"  → current shadcn-default look. Untouched. Safe baseline.
 *   • "cockpit"  → Jobs-era restraint pass: real type scale, hairlines over
 *                  tints, one emphasis per view, ~70% fewer icons, signature
 *                  spatial motion.
 *
 * The skin is applied to <html> as `data-ui-skin="..."`. Cockpit-only styles
 * live in src/index.css scoped behind `[data-ui-skin="cockpit"]`. Components
 * may also branch in TSX via `useUISkin()` for structural redesigns that
 * can't be expressed in CSS alone.
 *
 * Default now prefers "cockpit" for new sessions; users can still switch
 * and the choice remains persisted in localStorage.
 */
import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";

export type UISkin = "classic" | "cockpit";

interface UISkinContextValue {
  skin: UISkin;
  setSkin: (skin: UISkin) => void;
  toggleSkin: () => void;
}

const UISkinContext = createContext<UISkinContextValue | null>(null);
const STORAGE_KEY = "uiSkin";

function readInitial(): UISkin {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "cockpit" || v === "classic") return v;
  } catch {/* SSR / disabled storage */}
  return "cockpit";
}

export function UISkinProvider({ children }: { children: ReactNode }) {
  const [skin, setSkinState] = useState<UISkin>(readInitial);

  // Reflect the skin on <html> so CSS scoped behind [data-ui-skin="cockpit"]
  // takes effect everywhere — including portals (dialogs, sheets, toasts)
  // that mount outside the React tree.
  useEffect(() => {
    document.documentElement.setAttribute("data-ui-skin", skin);
    return () => {
      // Don't clear on unmount — provider lives for the app's lifetime.
    };
  }, [skin]);

  const setSkin = useCallback((next: UISkin) => {
    setSkinState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch {/* ignore */}
  }, []);

  const toggleSkin = useCallback(() => {
    setSkin(skin === "classic" ? "cockpit" : "classic");
  }, [skin, setSkin]);

  // Cross-tab sync — flipping the skin in one tab updates all open tabs.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === "cockpit" || e.newValue === "classic")) {
        setSkinState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return (
    <UISkinContext.Provider value={{ skin, setSkin, toggleSkin }}>
      {children}
    </UISkinContext.Provider>
  );
}

export function useUISkin() {
  const ctx = useContext(UISkinContext);
  if (!ctx) throw new Error("useUISkin must be used within UISkinProvider");
  return ctx;
}
